import {Localize} from "@dp-instant/i18n";
import {visibilityEffect} from "@dp-instant/ui";
import {UIConstants} from "@game/ui/constants";
import {tonConnectManager} from "@game/ton/TonConnectManager";
import {playUIClickSound} from "@game/helpers";
import cn from "classnames";
import {useState, useEffect} from "preact/hooks";
import "./WalletPopup.less";

type WalletPopupProps = {
    onBack: () => void;
};

function shortenAddress(addr: string): string {
    if (addr.length <= 12) return addr;
    return addr.slice(0, 6) + "..." + addr.slice(-4);
}

export const WalletPopup = ({onBack}: WalletPopupProps) => {
    const [invisible] = visibilityEffect(UIConstants.popup.startDelay);
    const [disconnecting, setDisconnecting] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [address, setAddress] = useState<string | null>(tonConnectManager.getAddress());
    const [loading, setLoading] = useState(!tonConnectManager.getAddress());
    const [tonBalance, setTonBalance] = useState<string | null>(null);

    // Listen for TonConnect session restore
    useEffect(() => {
        const unsub = tonConnectManager.onStatusChange((wallet) => {
            setAddress(wallet ? tonConnectManager.getAddress() : null);
            setLoading(false);
        });
        const timeout = setTimeout(() => setLoading(false), 2000);
        return () => { unsub(); clearTimeout(timeout); };
    }, []);

    useEffect(() => {
        if (address) {
            tonConnectManager.getBalance().then(b => setTonBalance(b));
        }
    }, [address]);

    const handleConnect = async () => {
        playUIClickSound();
        setConnecting(true);
        try {
            await tonConnectManager.connect();
            setAddress(tonConnectManager.getAddress());
        } catch {
            const tg = (window as any).Telegram?.WebApp;
            tg?.showAlert?.("Connection cancelled");
        }
        setConnecting(false);
    };

    const handleDisconnect = async () => {
        playUIClickSound();
        setDisconnecting(true);
        try {
            await tonConnectManager.disconnect();
        } catch {}
        onBack();
    };

    return (
        <div className="popups">
            <div className={cn("popup-wallet", {invisible})}>
                <div className="popup-wallet__handle" />
                <div className="popup-wallet__title">
                    {Localize.get("ui-wallet-title", "TON Wallet")}
                </div>
                <div className="popup-wallet__body">
                    {loading ? (
                        <div className="popup-wallet__no-wallet">...</div>
                    ) : address ? (
                        <>
                            <div className="popup-wallet__address">{shortenAddress(address)}</div>
                            {tonBalance !== null && (
                                <div className="popup-wallet__balance">{tonBalance} TON</div>
                            )}
                        </>
                    ) : (
                        <div className="popup-wallet__no-wallet">
                            {Localize.get("ui-wallet-not-connected", "No wallet connected")}
                        </div>
                    )}

                    {!loading && !address && (
                        <button
                            className="popup-wallet__connect"
                            disabled={connecting}
                            onClick={handleConnect}
                        >
                            {connecting ? "..." : Localize.get("ui-wallet-connect", "Connect Wallet")}
                        </button>
                    )}

                    {address && (
                        <button
                            className="popup-wallet__disconnect"
                            disabled={disconnecting}
                            onClick={handleDisconnect}
                        >
                            {disconnecting ? "..." : Localize.get("ui-wallet-disconnect", "Disconnect Wallet")}
                        </button>
                    )}

                    <button
                        className="popup-wallet__back"
                        onClick={() => { playUIClickSound(); onBack(); }}
                    >
                        {Localize.get("ui-common-back", "BACK")}
                    </button>
                </div>
            </div>
        </div>
    );
};
